import { Fragment, ReactNode } from 'react';
import { Styles } from 'react-select';

import { DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import styled from 'styled-components';

import { ActionText } from '@components/action-text';
import { Badge, BadgeVariant } from '@components/badge';
import { Button } from '@components/button';
import { useLocalStorageState } from '@components/component-helpers';
import { DragHandle } from '@components/drag-and-drop';
import { useFormContext } from '@components/form';
import { FormDataType, FormInstance } from '@components/form/create-form';
import { FormStoreInstance } from '@components/form/create-form-store';
import { IconButton } from '@components/icon-button';
import { IconFancyContainer, IconFancyContainerProps } from '@components/icon-fancy-container';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconArray } from '@components/icons/array';
import { BaseIcon } from '@components/icons/base';
import { IconDrag } from '@components/icons/drag';
import { IconDropdown } from '@components/icons/dropdown';
import { IconDropside } from '@components/icons/dropside';
import { IconField } from '@components/icons/field';
import { IconFieldCustom } from '@components/icons/field-custom';
import { IconInput } from '@components/icons/input';
import { IconIntegration } from '@components/icons/integration';
import { IconLabel } from '@components/icons/label';
import { IconSend } from '@components/icons/send';
import { IconTrash } from '@components/icons/trash';
import { IconVisibilityShown } from '@components/icons/visibility-shown';
import { IconWorkType } from '@components/icons/work-type';
import { Menu } from '@components/menu';
import { SELECT_ZINDEX } from '@components/select/styles';
import { SquareImage } from '@components/square-image';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { lineClamp } from '@styles/layout-helpers';
import { Color, theme } from '@styles/theme';
import { countTruthy } from '@utils/collections';
import { pxToRem } from '@utils/converters';
import { getKeys } from '@utils/objects';

import { DefaultValueField, ExampleValueField, getFieldIds } from './endpoint-fields';
import { useLists } from './integrations-hooks';
import type {
  Authentication,
  EndpointMethod,
  IntegrationEndpointDateVariable,
  IntegrationEndpointSelectVariable,
  IntegrationEndpointVariable,
  List,
} from './integrations-types';
import {
  Field,
  FieldIndentBorder,
  FieldIndentBorderContainer,
  Grid,
  TYPE_ICON_MAP,
} from './styles';

export const endpointColorMap: Record<EndpointMethod, Color> = {
  POST: 'elementBlue100',
  GET: 'elementGreen100',
  PATCH: 'elementOrange100',
  PUT: 'indigo100',
  DELETE: 'elementRed100',
};

export const AuthTypeMap: Record<Authentication['authType'], string> = {
  '': 'None',
  basic: 'Basic',
  bearer_token: 'Bearer token',
  oauth2: 'OAuth2',
  api_key: 'API key',
  authorization_header: 'Authorization header',
  jwt_token: 'JWT token',
  oauth_connect: 'OAuth',
};

export const CUSTOM_FIELD_PREFIX = '_custom_';

const methodTagColorMap: Record<EndpointMethod, BadgeVariant> = {
  POST: 'blue',
  GET: 'green',
  PATCH: 'orange',
  PUT: 'indigo',
  DELETE: 'red',
};

type VisibleColumnsType = {
  name: boolean;
  type: boolean;
  label: boolean;
  example: boolean;
  default: boolean;
  visible: boolean;
  format: boolean;
};

export const MethodTag = ({ method }: { method: EndpointMethod }) => (
  <Badge variant={methodTagColorMap[method]} data-testid="method" text={method} />
);

type ClampedTextProps = React.ComponentProps<typeof Text>;

export const ClampedText = (props: ClampedTextProps) => {
  return (
    <Text
      {...props}
      style={{
        ...lineClamp('-webkit-box', 1, true),
        paddingRight: pxToRem(6),
        ...props.style,
      }}
    />
  );
};

export const methodFancyContainerColorMap: Record<
  EndpointMethod,
  IconFancyContainerProps['variant']
> = {
  POST: 'blue',
  GET: 'green',
  PATCH: 'orange',
  PUT: 'indigo',
  DELETE: 'red',
};

type DraggableIconProps = {
  variant: IconFancyContainerProps['variant'];
  icon: ReactNode;
  dragAttributes?: DraggableAttributes;
  dragListeners?: SyntheticListenerMap;
};

export const DraggableIcon = ({
  variant,
  icon,
  dragAttributes,
  dragListeners,
}: DraggableIconProps) => {
  return (
    <DragHandle {...dragAttributes} {...dragListeners} $isDraggable={Boolean(dragListeners)}>
      <IconFancyContainer
        variant={variant}
        icon={dragListeners ? icon : <IconDrag />}
        hoverIcon={dragListeners ? <IconDrag /> : undefined}
      />
    </DragHandle>
  );
};

const parseArrayOfStrings = (value: unknown): string[] | null | undefined => {
  if (typeof value !== 'string') return undefined;
  const result = value
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  return result.length > 0 ? result : null;
};

const getFormEndpointVariable = ({
  formStore,
  allVariables,
  variable,
  parent,
  visibleColumns = {
    name: true,
    type: true,
    label: true,
    example: false,
    default: false,
    visible: false,
    format: false,
  },
}: {
  formStore: FormStoreInstance;
  allVariables: IntegrationEndpointVariable[];
  variable: IntegrationEndpointVariable;
  parent?: IntegrationEndpointVariable;
  visibleColumns?: VisibleColumnsType;
}) => {
  const formValues = formStore.getState().values;
  const name = parent ? generateTemplateName(allVariables, variable) : variable.name;

  const { typeId, optionsFromId, labelId, exampleId, defaultValueId, visibleId, formatId } =
    getFieldIds(name);
  const type = formValues[typeId] ?? variable.type;
  const label = formValues[labelId];
  const example = formValues[exampleId];
  const defaultValue = formValues[defaultValueId];
  const visible = formValues[visibleId];
  const format = formValues[formatId];

  const isArrayOfStrings = type === 'array_of_strings';
  const processedExample = isArrayOfStrings ? parseArrayOfStrings(example) : example;
  const processedDefaultValue = isArrayOfStrings ? parseArrayOfStrings(defaultValue) : defaultValue;

  const variableData = {
    name: variable.name,
    type: visibleColumns.type ? type ?? variable.type : variable.type,
    label: visibleColumns.label ? label ?? variable.label : variable.label,
    example: visibleColumns.example
      ? processedExample !== undefined
        ? processedExample
        : variable.example
      : variable.example,
    default_value: visibleColumns.default
      ? processedDefaultValue !== undefined
        ? processedDefaultValue
        : variable.default_value
      : variable.default_value,
    visible: visibleColumns.visible ? visible ?? variable.visible : variable.visible,
    ...(variable.jqQuery && { jqQuery: variable.jqQuery }),
    ...(type === 'date' && {
      format: visibleColumns.format
        ? format ?? (variable as IntegrationEndpointDateVariable).format
        : (variable as IntegrationEndpointDateVariable).format,
    }),
    ...(type === 'select' && {
      options_from: visibleColumns.type
        ? formValues[optionsFromId] ?? (variable as IntegrationEndpointSelectVariable).options_from
        : (variable as IntegrationEndpointSelectVariable).options_from,
    }),
    ...(type === 'array' &&
      'output_format' in variable &&
      variable.output_format && { output_format: variable.output_format }),
  } as IntegrationEndpointVariable;
  if (variable.template) {
    variableData.template = variable.template.map((templateVariable) => {
      return getFormEndpointVariable({
        formStore,
        allVariables,
        variable: templateVariable,
        parent: variable,
        visibleColumns,
      }) as IntegrationEndpointVariable;
    });
  }

  return variableData;
};

export const getFormEndpointVariables = ({
  formStore,
  endpointVariables,
  visibleColumns = {
    name: true,
    type: true,
    label: true,
    example: false,
    default: false,
    visible: false,
    format: false,
  },
}: {
  formStore: FormStoreInstance;
  endpointVariables: IntegrationEndpointVariable[];
  visibleColumns?: VisibleColumnsType;
}) => {
  const variables = endpointVariables.map((variable) => {
    return getFormEndpointVariable({
      formStore,
      allVariables: endpointVariables,
      variable,
      visibleColumns,
    });
  });
  return variables;
};

const flattenAllVariables = (
  allVariables: IntegrationEndpointVariable[],
  parentPrefix?: string
) => {
  const flat: { variable: IntegrationEndpointVariable; flatten: string }[] = [];

  allVariables.forEach((variable) => {
    const flattenName = parentPrefix
      ? `${parentPrefix}${nestedSeparator}${variable.name}`
      : variable.name;
    flat.push({ variable: variable, flatten: flattenName });
    if (variable.template) {
      flat.push(...flattenAllVariables(variable.template, flattenName));
    }
  });

  return flat;
};

const nestedSeparator = '__';
export const generateTemplateName = (
  allVariables: IntegrationEndpointVariable[],
  template: IntegrationEndpointVariable
) => {
  const flattenVariableList = flattenAllVariables(allVariables);
  return (
    flattenVariableList.find((flatten) => flatten.variable === template)?.flatten ?? template.name
  );
};

type CustomValueSidebarOptions = {
  exampleData?: any;
  onOpenCustomValueSidebar?: (path: string[], scopedData: any, existingValue?: any) => void;
};

type FieldTableProps<T> = {
  endpointId: string;
  endpointVariables: IntegrationEndpointVariable[];
  form: FormInstance<T>;
  visibleColumns?: VisibleColumnsType;
  showWebhookUrl?: boolean;
  showArrayType?: boolean;
  handleRemoveField?: (name: string) => void;
  requestType?: string;
  customValueOptions?: CustomValueSidebarOptions;
  fieldColumnLabel?: string;
  listTemplateId?: string;
};

const FieldTableHeaderItem = ({
  text,
  icon: Icon,
  iconSize = 16,
}: {
  text?: string;
  icon?: typeof BaseIcon;
  iconSize?: number;
}) => {
  return (
    <Field>
      <Stack space={1} orientation="horizontal" align="center">
        {Icon && <Icon color="elementPurple50" size={iconSize} />}
        {text && <Text text={text} font="semibold14" color="textSecondary" />}
      </Stack>
    </Field>
  );
};

export const FieldTable = <T extends FormDataType>({
  endpointId,
  endpointVariables,
  showWebhookUrl = false,
  showArrayType = false,
  visibleColumns = {
    name: true,
    type: true,
    label: true,
    example: true,
    default: false,
    visible: false,
    format: false,
  },
  handleRemoveField,
  requestType,
  customValueOptions,
  fieldColumnLabel,
  listTemplateId,
  ...rest
}: FieldTableProps<T>) => {
  const { t } = useScopedTranslate(locale);
  const { data: lists } = useLists(listTemplateId ? { templateId: listTemplateId } : undefined);

  const [collapsedArrays, setCollapsedArrays] = useLocalStorageState<{ [key: string]: string[] }>({
    key: 'integration-arrays-collapsed',
    defaultValue: {},
  });
  const updateCollapsed = (field: string) => {
    const isCollapsed = (collapsedArrays[endpointId] ?? []).includes(field);
    const newCollapsed = { ...collapsedArrays };
    if (isCollapsed) {
      newCollapsed[endpointId] = newCollapsed[endpointId].filter((name) => name !== field);
      if (newCollapsed[endpointId].length === 0) {
        delete newCollapsed[endpointId];
      }
    } else if (!newCollapsed[endpointId]) {
      newCollapsed[endpointId] = [field];
    } else {
      newCollapsed[endpointId].push(field);
    }
    setCollapsedArrays(newCollapsed);
  };
  const numColumns = getKeys(visibleColumns).filter(
    (key) => key !== 'visible' && key !== 'format' && visibleColumns[key]
  ).length;

  const numSmallColumns = countTruthy(visibleColumns.visible, handleRemoveField);

  return (
    <Grid $numColumns={numColumns} $numSmallColumns={numSmallColumns}>
      {visibleColumns.name && (
        <FieldTableHeaderItem text={fieldColumnLabel ?? t('field')} icon={IconField} />
      )}
      {visibleColumns.type && <FieldTableHeaderItem text={t('type')} icon={IconWorkType} />}
      {visibleColumns.label && <FieldTableHeaderItem text={t('label')} icon={IconLabel} />}
      {visibleColumns.example && <FieldTableHeaderItem text={t('testRequest')} icon={IconSend} />}
      {visibleColumns.default && <FieldTableHeaderItem text={t('defaultValue')} icon={IconInput} />}
      {handleRemoveField && <FieldTableHeaderItem />}
      {visibleColumns.visible && <FieldTableHeaderItem icon={IconVisibilityShown} iconSize={24} />}
      {endpointVariables.map((field) => {
        const cannotBeDeleted = field.type === 'array' && endpointVariables.length === 1;
        return (
          <FieldVariable
            key={field.name + field.type}
            allFields={endpointVariables}
            field={field}
            lists={lists ?? []}
            visibleColumns={visibleColumns}
            handleRemoveField={handleRemoveField}
            showWebhookUrl={showWebhookUrl}
            showArrayType={showArrayType}
            collapsedArrays={collapsedArrays[endpointId] ?? []}
            updateCollapsed={updateCollapsed}
            cannotBeDeleted={cannotBeDeleted}
            requestType={requestType}
            customValueOptions={customValueOptions}
            currentPath={[]}
            {...rest}
          />
        );
      })}
    </Grid>
  );
};

type FieldVariableProps<T> = {
  form: FormInstance<T>;
  visibleColumns: VisibleColumnsType;
  showWebhookUrl?: boolean;
  showArrayType?: boolean;
  handleRemoveField?: (name: string) => void;
  allFields: IntegrationEndpointVariable[];
  field: IntegrationEndpointVariable;
  lists: List[];

  overwriteName?: string;
  arrayDepth?: number;
  isFirstVariable?: boolean;
  isLastVariableArray?: boolean[];
  collapsedArrays: string[];
  updateCollapsed: (field: string) => void;
  cannotBeDeleted?: boolean;
  requestType?: string;
  customValueOptions?: CustomValueSidebarOptions;
  currentPath?: string[];
};

const FieldVariable = <T extends FormDataType>({
  allFields,
  field,
  lists,
  showWebhookUrl = false,
  showArrayType = false,
  visibleColumns,
  handleRemoveField,
  overwriteName,
  arrayDepth,
  isFirstVariable,
  isLastVariableArray,
  collapsedArrays,
  updateCollapsed,
  cannotBeDeleted,
  requestType,
  customValueOptions,
  currentPath = [],
  ...rest
}: FieldVariableProps<T>) => {
  const exampleData = customValueOptions?.exampleData;
  const onOpenCustomValueSidebar = customValueOptions?.onOpenCustomValueSidebar;
  const { t } = useScopedTranslate(locale);
  const isCollapsed = collapsedArrays.includes(field.name);
  const fieldName = overwriteName ?? field.name;

  const getScopedDataForArray = () => {
    if (!exampleData) return null;

    // Navigate to the current level in exampleData
    let scopedData = exampleData;

    for (const pathPart of currentPath) {
      scopedData = scopedData?.[pathPart];
      // If we've navigated to an array (template level), use first item for further navigation
      if (Array.isArray(scopedData) && scopedData.length > 0) {
        scopedData = scopedData[0];
      }
    }

    // Use the original field name (overwriteName) when looking up in example data
    // because field.name may have path prefix (e.g., 'users__devices') while
    // the actual data property is just 'devices'
    const lookupName = overwriteName ?? field.name;
    const arrayData = scopedData?.[lookupName];

    if (Array.isArray(arrayData) && arrayData.length > 0) {
      // Return the first item if it's an array
      return arrayData[0];
    }

    return null;
  };

  const handleAddCustomValue = () => {
    if (onOpenCustomValueSidebar) {
      const scopedData = getScopedDataForArray();
      if (scopedData) {
        // Use original field name (overwriteName) for the path, not the prefixed template name
        const newPath = [...currentPath, overwriteName ?? field.name];
        onOpenCustomValueSidebar(newPath, scopedData);
      }
    }
  };

  const variableName =
    field.type === 'array' ? (
      <Stack orientation="horizontal" space={2} align="center" justify="space-between">
        <Button
          variant="secondary"
          iconLeft={<IconArray />}
          text={`${fieldName.toUpperCase()} • ${(field.template ?? []).length}`}
          iconRight={isCollapsed ? <IconDropside /> : <IconDropdown />}
          onClick={() => updateCollapsed(field.name)}
        />
        {exampleData && onOpenCustomValueSidebar && getScopedDataForArray() && (
          <Menu
            buttonProps={{
              iconLeft: <IconAddPlus />,
              variant: 'secondary',
              iconRight: <IconDropdown />,
              size: 'sm',
            }}
          >
            <Menu.Item
              icon={<IconFieldCustom />}
              label={t('addCustomValue')}
              onSelect={handleAddCustomValue}
            />
          </Menu>
        )}
      </Stack>
    ) : overwriteName ? (
      <Text text={overwriteName} font="regular14" ellipsis tooltip={overwriteName} />
    ) : undefined;
  const shouldShowArrayItems = field.type === 'array' && !isCollapsed;

  return (
    <>
      <FieldRow
        field={field}
        lists={lists}
        visibleColumns={visibleColumns}
        handleRemoveField={handleRemoveField}
        showWebhookUrl={showWebhookUrl}
        showArrayType={showArrayType}
        overwriteName={variableName}
        arrayDepth={arrayDepth}
        isFirstVariable={isFirstVariable}
        isLastVariableArray={isLastVariableArray}
        isCollapsed={isCollapsed}
        cannotBeDeleted={cannotBeDeleted}
        requestType={requestType}
        exampleData={exampleData}
        onOpenCustomValueSidebar={onOpenCustomValueSidebar}
        currentPath={currentPath}
        {...rest}
      />
      {shouldShowArrayItems &&
        field.template?.map((templateField, index) => {
          const templateFieldName = generateTemplateName(allFields, templateField);
          const depth = (arrayDepth ?? -1) + 1;
          const isLast = index === (field.template ?? []).length - 1;

          const lastVariablesArray = [...(isLastVariableArray ?? []), isLast];
          // Use overwriteName (original field name) for path, not field.name (which may be prefixed)
          const newPath = [...currentPath, overwriteName ?? field.name];

          return (
            <FieldVariable
              key={templateField.name}
              allFields={allFields}
              field={{ ...templateField, name: templateFieldName }}
              lists={lists}
              visibleColumns={visibleColumns}
              handleRemoveField={handleRemoveField}
              showWebhookUrl={showWebhookUrl}
              showArrayType={showArrayType}
              overwriteName={templateField.name}
              arrayDepth={depth}
              isFirstVariable={index === 0}
              isLastVariableArray={lastVariablesArray}
              collapsedArrays={collapsedArrays}
              updateCollapsed={updateCollapsed}
              requestType={requestType}
              customValueOptions={customValueOptions}
              currentPath={newPath}
              {...rest}
            />
          );
        })}
    </>
  );
};

const FieldRowBorderIndent = ({
  hasTemplate,
  depth,
  isFirst = false,
  isLastVariableArray = [],
  isCollapsed = false,
}: {
  hasTemplate: boolean;
  depth?: number;
  isFirst?: boolean;
  isLastVariableArray?: boolean[];
  isCollapsed?: boolean;
}) => {
  const arrayBorderColors = [
    theme.color.uiTrans20,
    theme.color.blueTrans20,
    theme.color.greenTrans20,
    theme.color.orangeTrans20,
  ];
  if (depth === undefined) {
    return null;
  }
  const isLastVariableEmptyArrayOrOther = !hasTemplate || isCollapsed;

  return (
    <FieldIndentBorderContainer $depth={depth}>
      {[...Array(depth + 1)].map((_, index) => {
        // Line ends only if all items from index to depth are last
        const isLastVariable =
          isLastVariableArray.slice(index, depth + 1).every(Boolean) &&
          isLastVariableEmptyArrayOrOther;
        return (
          <FieldIndentBorder
            key={index}
            $color={arrayBorderColors[index % arrayBorderColors.length]}
            $isFirst={index === depth && isFirst}
            $isLast={isLastVariable}
          />
        );
      })}
    </FieldIndentBorderContainer>
  );
};

const dropdownMenuStyles: Styles<any, boolean> = {
  menuPortal: (provided) => ({
    ...provided,
    zIndex: SELECT_ZINDEX,
    '@media (max-width: 1900px)': {
      minWidth: '320px',
    },
  }),
};

type FieldRowProps<T> = {
  field: IntegrationEndpointVariable;
  lists: List[];
  form: FormInstance<T>;
  showWebhookUrl?: boolean;
  showArrayType?: boolean;
  visibleColumns: VisibleColumnsType;
  handleRemoveField?: (name: string) => void;
  overwriteName?: JSX.Element;
  arrayDepth?: number;
  isFirstVariable?: boolean;
  isLastVariableArray?: boolean[];
  isCollapsed?: boolean;
  cannotBeDeleted?: boolean;
  requestType?: string;
  exampleData?: any;
  onOpenCustomValueSidebar?: (path: string[], scopedData: any, existingValue?: any) => void;
  currentPath?: string[];
};

export const useFieldTypes = (excluded: Array<string> = []) => {
  const { t } = useScopedTranslate(locale);
  return Object.entries(TYPE_ICON_MAP)
    .filter(([type, _]) => !excluded.includes(type))
    .map(([type, icon]) => ({
      label: (
        <Stack orientation="horizontal" align="center" space={2}>
          {icon}
          {t(type as IntegrationEndpointVariable['type'])}
        </Stack>
      ),
      value: type as IntegrationEndpointVariable['type'],
    }));
};

export const FieldMinHeightWrapper = styled.div({
  height: pxToRem(40),
  display: 'flex',
  alignItems: 'center',
});

const FieldRow = <T extends FormDataType>({
  field,
  lists = [],
  form: Form,
  showWebhookUrl = false,
  showArrayType = false,
  visibleColumns,
  handleRemoveField,
  overwriteName,
  arrayDepth,
  isFirstVariable = false,
  isLastVariableArray,
  isCollapsed,
  cannotBeDeleted = false,
  requestType,
  exampleData,
  onOpenCustomValueSidebar,
  currentPath = [],
}: FieldRowProps<T>) => {
  const {
    typeId,
    optionsFromId,
    labelId,
    exampleId,
    defaultValueId,
    visibleId,
    formatId,
    jqQueryId,
  } = getFieldIds(field.name);
  const { store } = useFormContext();
  const formValues = store.useFormValues();
  const fieldType = (formValues[typeId] as IntegrationEndpointVariable['type']) ?? field.type;
  const isArray = field.type === 'array' && (field.template ?? []).length > 0;
  const excludeTypes = [
    ...(!showArrayType && !isArray ? ['array'] : []),
    ...(!showWebhookUrl ? ['webhookUrl'] : []),
  ];

  const types = useFieldTypes(excludeTypes);

  const isCustomResponseValue = Boolean(field.jqQuery);

  const handleEditCustomValue = () => {
    if (isCustomResponseValue && onOpenCustomValueSidebar && exampleData) {
      // Use the EXACT SAME algorithm as getScopedDataForArray
      let scopedData = exampleData;

      for (const pathPart of currentPath) {
        scopedData = scopedData?.[pathPart];
        // If we've navigated to an array (template level), use first item for further navigation
        if (Array.isArray(scopedData) && scopedData.length > 0) {
          scopedData = scopedData[0];
        }
      }

      // Extract the original field name if this is a nested field
      // Flattened names look like: items__{custom_field_name}
      // We need just the last part: {custom_field_name}
      const originalName =
        currentPath.length > 0
          ? field.name.split(`${currentPath.join('__')}__`)[1] || field.name
          : field.name;

      const fieldWithOriginalName = {
        ...field,
        name: originalName,
      };

      onOpenCustomValueSidebar(currentPath, scopedData, fieldWithOriginalName);
    }
  };

  const defaultValue = fieldType === field.type ? field.default_value : undefined;
  const valueField = { ...field, type: fieldType } as IntegrationEndpointVariable;
  const formats = [
    {
      value: 'DDMMYYYY',
      label: 'DD MM YYYY',
    },
    {
      value: 'MMDDYYYY',
      label: 'MM DD YYYY',
    },
    {
      value: 'YYYYMMDD',
      label: 'YYYY MM DD',
    },
  ];
  const displayName =
    typeof overwriteName === 'string'
      ? overwriteName
      : field.name.includes(CUSTOM_FIELD_PREFIX)
      ? field.name.split('__').find((part) => part.startsWith(CUSTOM_FIELD_PREFIX)) || field.name
      : field.name;

  return (
    <Fragment key={field.name}>
      {visibleColumns.name && (
        <Field $depth={arrayDepth}>
          <FieldRowBorderIndent
            hasTemplate={(field.template ?? []).length > 0}
            depth={arrayDepth}
            isFirst={isFirstVariable}
            isLastVariableArray={isLastVariableArray}
            isCollapsed={isCollapsed}
          />
          <FieldMinHeightWrapper>
            {overwriteName && !isCustomResponseValue ? (
              overwriteName
            ) : isCustomResponseValue ? (
              <ActionText
                font="regular14"
                color="elementIndigo100"
                hoverColor="elementIndigo100"
                onClick={handleEditCustomValue}
                iconLeft={<IconFieldCustom size={16} />}
                text={displayName}
                title={displayName}
                ellipsis
              />
            ) : (
              <Text text={field.name} font="regular14" ellipsis tooltip={field.name} />
            )}
          </FieldMinHeightWrapper>
        </Field>
      )}
      {field.jqQuery && <Form.HiddenField id={jqQueryId} defaultValue={field.jqQuery} />}
      {visibleColumns.type && (
        <Field>
          <>
            <Form.Select
              label=""
              id={typeId}
              options={types}
              defaultValue={field.type}
              isClearable={false}
              isDisabled={isArray || (field.type === 'file' && requestType === 'file')}
              styles={dropdownMenuStyles}
            />
            {fieldType === 'select' && (
              <Form.Select
                label=""
                id={optionsFromId}
                options={lists.map((list) => ({ value: list.id, label: list.name }))}
                defaultValue={(field as IntegrationEndpointSelectVariable).options_from}
                isClearable={false}
                styles={dropdownMenuStyles}
              />
            )}
            {fieldType === 'date' && visibleColumns.format && (
              <Form.Select
                label=""
                id={formatId}
                options={formats}
                defaultValue={(field as IntegrationEndpointDateVariable).format ?? 'YYYYMMDD'}
                isClearable={false}
                styles={dropdownMenuStyles}
              />
            )}
          </>
        </Field>
      )}
      {visibleColumns.label && (
        <Field>
          <Form.TextField id={labelId} label="label" hideLabel defaultValue={field.label} />
        </Field>
      )}
      {visibleColumns.example && (
        <Field>
          <ExampleValueField
            id={exampleId}
            field={valueField}
            form={Form}
            defaultValue={field.example}
          />
        </Field>
      )}
      {visibleColumns.default && (
        <Field>
          <DefaultValueField
            key={`${defaultValueId}-${fieldType}-${field.default_value}`}
            id={defaultValueId}
            field={valueField}
            form={Form}
            defaultValue={defaultValue}
          />
        </Field>
      )}
      {handleRemoveField && (
        <Field>
          <FieldMinHeightWrapper>
            <IconButton
              icon={<IconTrash />}
              onClick={() => handleRemoveField(field.name)}
              variant="ghost-purple"
              disabled={cannotBeDeleted}
            />
          </FieldMinHeightWrapper>
        </Field>
      )}
      {visibleColumns.visible && (
        <Field>
          <FieldMinHeightWrapper>
            <Form.Switch
              id={visibleId}
              label={field.name}
              labelVisible={false}
              defaultValue={field.visible ?? true}
            />
          </FieldMinHeightWrapper>
        </Field>
      )}
    </Fragment>
  );
};

export const getVariableCount = (variables?: IntegrationEndpointVariable[]) => {
  return flattenAllVariables(variables ?? []).length;
};

const locale = {
  en: {
    array: 'Object array',
    array_of_strings: 'String array',
    field: 'Field',
    type: 'Type',
    label: 'Label',
    testRequest: 'Test request',
    defaultValue: 'Default value',
    text: 'Single line text',
    paragraph: 'Long text',
    date: 'Date',
    email: 'Email',
    phone: 'Phone number',
    number: 'Number (Integer)',
    select: 'Select',
    file: 'File',
    boolean: 'Boolean',
    decimal: 'Decimal (Float)',
    webhookUrl: 'Unique webhook url',
    addCustomValue: 'Add a custom list value',
  },
};

export const generateFieldName = () => {
  return `${CUSTOM_FIELD_PREFIX}${Date.now().toString(36)}`;
};

type IntegrationIconProps = {
  src?: string | null;
  size?: 'sm' | 'md' | 'lg';
};

export const IntegrationIcon = ({ src, size }: IntegrationIconProps) => {
  return src ? <SquareImage src={src} size={size} /> : <IconIntegration />;
};

export type IntegrationItem<Data extends { shortId: string; order?: number | null }> =
  | { id: string; type: 'endpoint'; data: Data }
  | { id: string; type: 'webhook'; data: Data };

export const combineEndpointsAndWebhooks = <
  Endpoint extends { shortId: string; order?: number | null },
  Webhook extends { shortId: string; order?: number | null }
>(
  endpoints: Endpoint[],
  webhooks: Webhook[]
): IntegrationItem<Endpoint | Webhook>[] => {
  const endpointItems: IntegrationItem<Endpoint | Webhook>[] = endpoints.map((endpoint) => ({
    id: endpoint.shortId,
    type: 'endpoint',
    data: endpoint,
  }));
  const webhookItems: IntegrationItem<Endpoint | Webhook>[] = webhooks.map((webhook) => ({
    id: webhook.shortId,
    type: 'webhook',
    data: webhook,
  }));
  return [...endpointItems, ...webhookItems].sort(
    (itemA, itemB) => (itemA.data.order ?? 0) - (itemB.data.order ?? 0)
  );
};
